// frontend/src/services/groupService.js

import { getJWTSession } from "../utils/useJWTSession";

/**
 * Elimina un grupo de forma permanente.
 * Requiere que el usuario autenticado sea propietario del grupo.
 *
 * @param {number} groupId - ID del grupo a eliminar.
 * @throws {Error} Si falla la autenticación o el backend responde con error.
 */
export const deleteGroup = async (groupId) => {
  const token = getJWTSession();
  if (!token) throw new Error("Authentication required");

  const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/group/id/${groupId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 409) {
    throw new Error("Group is closed and cannot be deleted.");
  }

  if (res.status === 404) {
    throw new Error("Group not found or access denied.");
  }

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    throw new Error(text || "Failed to delete the group.");
  }
};

/**
 * Obtiene información de un grupo por su hash (grp_ o inv_).
 *
 * @param {string} hash - Hash principal del grupo.
 * @returns {Promise<Object>} - Datos del grupo.
 * @throws {Error} Si no se puede recuperar la información.
 */
export async function fetchGroupByHash(hash) {
  const token = getJWTSession();

  const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/user/group/${hash}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => null);
    throw new Error(errorText || "Failed to fetch group");
  }

  return await res.json();
}

// Función para actualizar el estado del grupo
export async function updateGroupState(state, accessHash) { // Only need accessHash now
  const url = `${process.env.REACT_APP_API_BASE}/api/group/state`;
  
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-access-hash': accessHash, // Siempre envía el hash de acceso para esta operación
      },
      body: JSON.stringify({ state }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating group state:', error);
    throw error;
  }
}